import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import {
  Package,
  MapPin,
  Calendar,
  DollarSign,
  CheckCircle,
  AlertCircle,
  Clock,
  X,
  Users,
  FileText,
  Truck,
  Shield,
  Upload,
  Filter,
  Search,
  ChevronLeft,
  ChevronRight,
  Plus
} from 'lucide-react';
import { agreementsAPI, disputesAPI } from '../services/api';
import { useThemeColors } from '../theme/ThemeContext';
import ConfirmationModal from '../components/ConfirmationModal';
import DisputeModal from '../components/DisputeModal';
import SEO from '../components/SEO';

const Agreements = () => {
  const colors = useThemeColors();
  const [activeTab, setActiveTab] = useState('active');
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [selectedAgreement, setSelectedAgreement] = useState(null);
  const [evidenceFiles, setEvidenceFiles] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [searchTerm, setSearchTerm] = useState('');

  // Modal states for confirmation actions
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationModalConfig, setConfirmationModalConfig] = useState({});
  const [confirmationImages, setConfirmationImages] = useState([]);
  const [confirmationDescription, setConfirmationDescription] = useState('');

  const queryClient = useQueryClient();

  // Fetch user agreements
  const {
    data: apiResponse,
    isLoading,
    refetch,
    error,
  } = useQuery(
    ['userAgreements', activeTab, currentPage, itemsPerPage, searchTerm],
    () => {
      return agreementsAPI.getUserAgreements({
        status: activeTab === 'all' ? '' : activeTab,
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm
      });
    },
    {
      select: (response) => {        
        return {
          agreements: response?.data.data.agreements || [],
          pagination: response?.data.data.pagination,
          statusCounts: response?.data.data.statusCounts || {}
        };
      },
      onError: (error) => {
        console.error('Frontend: Agreements API error:', error);
      }
    }
  );

  const agreements = apiResponse?.agreements || [];
  const pagination = apiResponse?.pagination;
  const statusCounts = apiResponse?.statusCounts || {};

  const confirmPickupMutation = useMutation(agreementsAPI.confirmPickup, {
    onSuccess: () => {
      toast.success('Pickup confirmed successfully!');
      queryClient.invalidateQueries('userAgreements');
      refetch();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to confirm pickup');
    },
  });

  const markDeliveredMutation = useMutation(agreementsAPI.markDelivered, {
    onSuccess: () => {
      toast.success('Delivery marked successfully! Waiting for receiver confirmation.');
      queryClient.invalidateQueries('userAgreements');
      refetch();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to mark delivery');
    },
  });

  const confirmDeliveryMutation = useMutation(agreementsAPI.confirmDelivery, {
    onSuccess: () => {
      toast.success('Delivery confirmed successfully! Agreement completed.');
      queryClient.invalidateQueries('userAgreements');
      refetch();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to confirm delivery');
    },
  });

  const downloadReceiptMutation = useMutation(
    (agreementId) => agreementsAPI.generateReceipt(agreementId),
    {
      onSuccess: (response, agreementId) => {
        // Axios returns the blob in response.data
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = `TT-${agreementId}.pdf`; // consistent with backend
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        window.URL.revokeObjectURL(url);

        toast.success('Receipt downloaded successfully!');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to download receipt');
      },
    },
  );
  const isDownloading = downloadReceiptMutation.isLoading;
  const cancelAgreementMutation = useMutation(agreementsAPI.cancelAgreement, {
    onSuccess: () => {
      toast.success('Agreement cancelled successfully!');
      queryClient.invalidateQueries('userAgreements');
      refetch();
      setShowDisputeModal(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to cancel agreement');
    },
  });

  const createDisputeMutation = useMutation(disputesAPI.createDispute, {
    onSuccess: () => {
      toast.success('Dispute created successfully!');
      queryClient.invalidateQueries('userAgreements');
      refetch();
      setShowDisputeModal(false);
      setSelectedAgreement(null);
      setEvidenceFiles([]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to create dispute');
    },
  });

  const handleConfirmPickup = (agreementId) => {
    const agreement = agreements.find(a => a.id === agreementId);
    if (!agreement) return;

    setConfirmationModalConfig({
      agreementId,
      action: 'confirmPickup',
      title: 'Confirm Pickup',
      message: 'Have you picked up the parcel from the sender? This confirms the delivery has started.',
      confirmText: 'Confirm Pickup',
      type: 'success',
      showImageUpload: true,
      showInput: true,
      inputPlaceholder: 'Add any notes about the pickup...',
      additionalInfo: {
        label: 'Route',
        value: `${agreement.parcel?.pickup_city || agreement.trip?.origin_city || 'Origin'} → ${agreement.parcel?.dropoff_city || agreement.trip?.destination_city || 'Destination'}`,
        note: 'Please ensure you have the parcel before confirming'
      }
    });
    setConfirmationImages([]);
    setConfirmationDescription('');
    setShowConfirmationModal(true);
  };

  const handleMarkDelivered = (agreementId) => {
    const agreement = agreements.find(a => a.id === agreementId);
    if (!agreement) return;

    setConfirmationModalConfig({
      agreementId,
      action: 'markDelivered',
      title: 'Mark as Delivered',
      message: 'Have you delivered the parcel to the receiver? The receiver will need to confirm receipt.',
      confirmText: 'Mark as Delivered',
      type: 'warning',
      showImageUpload: true,
      showInput: true,
      inputPlaceholder: 'Add delivery notes, receiver details, etc...',
      additionalInfo: {
        label: 'Destination',
        value: `${agreement.parcel?.dropoff_city || agreement.trip?.destination_city || 'Destination'}`,
        note: 'Receiver must confirm to complete the delivery'
      }
    });
    setConfirmationImages([]);
    setConfirmationDescription('');
    setShowConfirmationModal(true);
  };

  const handleConfirmDelivery = (agreementId) => {
    const agreement = agreements.find(a => a.id === agreementId);
    if (!agreement) return;

    setConfirmationModalConfig({
      agreementId,
      action: 'confirmDelivery',
      title: 'Confirm Delivery',
      message: 'Have you received the parcel? This will complete the agreement and release all funds.',
      confirmText: 'Confirm Receipt',
      type: 'info',
      showImageUpload: true,
      showInput: true,
      inputPlaceholder: 'Add any notes about the received parcel...',
      additionalInfo: {
        label: 'From',
        value: `${agreement.traveller?.first_name} ${agreement.traveller?.last_name}`,
        note: 'This action completes the delivery agreement'
      }
    });
    setConfirmationImages([]);
    setConfirmationDescription('');
    setShowConfirmationModal(true);
  };

  const handleCancelAgreement = (agreementId, reason) => {
    const agreement = agreements.find(a => a.id === agreementId);
    if (!agreement) return;

    setConfirmationModalConfig({
      agreementId,
      action: 'cancel',
      title: 'Cancel Agreement',
      message: 'Are you sure you want to cancel this agreement? This action cannot be undone.',
      confirmText: 'Cancel Agreement',
      type: 'error',
      showInput: true,
      inputPlaceholder: 'Please provide a reason for cancellation...',
      additionalInfo: {
        label: 'Agreement',
        value: `Agreement #${agreement.id}`,
        note: 'Cancellation may affect your reputation'
      }
    });
    setConfirmationImages([]);
    setConfirmationDescription('');
    setShowConfirmationModal(true);
  };

  const handleConfirmationSubmit = (data) => {
    // console.log('Confirmation submit data:', data);
    
    const { agreementId, action } = confirmationModalConfig;
    const { description, images } = data;
    // Always create FormData for consistent multipart handling
    const formData = new FormData();
    formData.append('description', description || '');
    if (images && images.length > 0) {
      images.forEach((image, index) => {
        if (image.file) {
          formData.append(`images`, image.file);
          console.log(`Appended image ${index} to FormData`);
        } else {
          console.error(`Image ${index} has no file property!`);
        }
      });
    }

    switch (action) {
      case 'confirmPickup':
        confirmPickupMutation.mutate({ id: agreementId, data: formData }, {
          onSuccess: () => {
            setShowConfirmationModal(false);
          }
        });
        break;
      case 'markDelivered':
        markDeliveredMutation.mutate({ id: agreementId, data: formData }, {
          onSuccess: () => {
            setShowConfirmationModal(false);
          }
        });
        break;
      case 'confirmDelivery':
        confirmDeliveryMutation.mutate({ id: agreementId, data: formData }, {
          onSuccess: () => {
            setShowConfirmationModal(false);
          }
        });
        break;
      case 'cancel':
        cancelAgreementMutation.mutate({ agreementId, reason: description }, {
          onSuccess: () => {
            setShowConfirmationModal(false);
          }
        });
        break;
      default:
        break;
    }
  };

  const handleCreateDispute = (disputeData) => {
    if (!selectedAgreement) return;

    const { files, ...data } = disputeData;
    createDisputeMutation.mutate(data, {
      // Pass files separately as expected by the API
      files: files,
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-blue-100 text-blue-800';
      case 'in_transit':
        return 'bg-yellow-100 text-yellow-800';
      case 'delivered':
        return 'bg-purple-100 text-purple-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'disputed':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <Clock className="h-4 w-4" />;
      case 'in_transit':
        return <Package className="h-4 w-4" />;
      case 'delivered':
        return <CheckCircle className="h-4 w-4" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4" />;
      case 'cancelled':
        return <X className="h-4 w-4" />;
      case 'disputed':
        return <AlertCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const filteredAgreements = agreements.length > 0 && agreements?.filter(agreement => {
    if (activeTab === 'all') return true;
    return agreement.status === activeTab;
  }) || [];

  // Pagination calculations
  const totalPages = Math.ceil(filteredAgreements.length / itemsPerPage);
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, filteredAgreements.length);

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page
  };

  const stats = {
    total: statusCounts.total || 0,
    active: statusCounts.active || 0,
    in_transit: statusCounts.in_transit || 0,
    delivered: statusCounts.delivered || 0,
    completed: statusCounts.completed || 0,
    cancelled: statusCounts.cancelled || 0,
    disputed: statusCounts.disputed || 0,
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 overflow-x-hidden">
      <SEO 
        title="Agreements" 
        description="Manage your delivery agreements, track package status, and handle transactions on TripTrot"
        keywords={['delivery agreements', 'package tracking', 'transaction management', 'parcel delivery']}
        canonical="/agreements"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "Delivery Agreements",
          "description": "List of user delivery agreements and transactions"
        }}
      />
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-theme-primary">Agreements</h1>
        <p className="mt-2 text-theme-secondary">Manage your active delivery agreements and track progress</p>
      </div>

      {/* Search and Filters */}
      <div className="mb-8">
        {/* Main Search Card */}
        <div 
          className="rounded-2xl shadow-xl transition-all duration-300 overflow-hidden"
          style={{ 
            backgroundColor: colors.card, 
            borderColor: colors.border 
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = colors.primary[300];
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = colors.border;
          }}
        >
          {/* Top accent bar */}
          <div className="h-1" />
          
          {/* Content */}
          <div className="p-6">
            {/* Search Row */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search 
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5" 
                  style={{ color: colors.text.secondary }}
                />
                <input
                  type="text"
                  placeholder="Search agreements..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-12 pr-4 py-3 w-full rounded-xl transition-all duration-200 border"
                  style={{
                    backgroundColor: colors.neutral[50],
                    borderColor: colors.border,
                    color: colors.text.primary
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = colors.primary[500];
                    e.currentTarget.style.boxShadow = `0 0 0 2px ${colors.primary[200]}`;
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = colors.border;
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
        {Object.entries({
          total: { icon: Package, color: 'primary', label: 'Total' },
          active: { icon: Clock, color: 'primary', label: 'Active' },
          in_transit: { icon: Package, color: 'warning', label: 'In Transit' },
          delivered: { icon: CheckCircle, color: 'success', label: 'Delivered' },
          completed: { icon: CheckCircle, color: 'success', label: 'Completed' },
          cancelled: { icon: X, color: 'error', label: 'Cancelled' },
          disputed: { icon: AlertCircle, color: 'warning', label: 'Disputed' },
        }).map(([key, { icon: Icon, color, label }]) => (
          <div key={key} className="bg-theme-card p-4 rounded-lg border border-theme-light">
            <div className="flex items-center">
              <div className={`p-2 bg-theme-${color}-100 rounded-lg`}>
                <Icon className={`h-5 w-5 text-theme-${color}`} />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-theme-secondary">{label}</p>
                <p className="text-lg font-semibold text-theme-primary">{stats[key]}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Sleek Tabs */}
      <div className="mb-8">
        {/* Mobile: Vertical tabs | Desktop: Horizontal tabs */}
        <div className="sm:hidden">
          <div 
            className="rounded-2xl p-2 space-y-1 border"
            style={{ 
              backgroundColor: colors.card, 
              borderColor: colors.border 
            }}
          >
            {[
              { key: 'all', label: 'All', icon: FileText },
              { key: 'active', label: 'Active', icon: Clock },
              { key: 'in_transit', label: 'In Transit', icon: Truck },
              { key: 'delivered', label: 'Delivered', icon: Package },
              { key: 'completed', label: 'Completed', icon: CheckCircle },
              { key: 'cancelled', label: 'Cancelled', icon: X },
              { key: 'disputed', label: 'Disputed', icon: AlertCircle }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center w-full px-3 py-3 rounded-xl font-medium text-sm transition-all duration-200 ${
                  activeTab === tab.key
                    ? 'shadow-lg'
                    : ''
                }`}
                style={{
                  backgroundColor: activeTab === tab.key ? colors.primary[600] : 'transparent',
                  color: activeTab === tab.key ? 'white' : colors.text.secondary
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== tab.key) {
                    e.currentTarget.style.backgroundColor = colors.neutral[50];
                    e.currentTarget.style.color = colors.primary[600];
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== tab.key) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = colors.text.secondary;
                  }
                }}
              >
                <tab.icon className="h-4 w-4 mr-3 flex-shrink-0" />
                <span className="flex-1 text-left">{tab.label}</span>
                {tab.key !== 'all' && stats[tab.key] > 0 && (
                  <span className={`px-2 py-0.5 rounded-full text-xs flex-shrink-0 ${
                    activeTab === tab.key 
                      ? 'bg-white/20 text-white' 
                      : 'bg-theme-neutral-25 text-theme-secondary'
                  }`}>
                    {stats[tab.key]}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Desktop version */}
        <div className="hidden sm:block">
          <div 
            className="rounded-2xl p-2 flex flex-wrap gap-2 items-center justify-around"
            style={{ 
              backgroundColor: colors.card, 
              borderColor: colors.border 
            }}
          >
            {[
              { key: 'all', label: 'All', icon: FileText },
              { key: 'active', label: 'Active', icon: Clock },
              { key: 'in_transit', label: 'In Transit', icon: Truck },
              { key: 'delivered', label: 'Delivered', icon: Package },
              { key: 'completed', label: 'Completed', icon: CheckCircle },
              { key: 'cancelled', label: 'Cancelled', icon: X },
              { key: 'disputed', label: 'Disputed', icon: AlertCircle }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center px-4 py-2 rounded-xl font-medium text-sm transition-all duration-200 flex-1 min-w-0 sm:flex-none ${
                  activeTab === tab.key
                    ? 'shadow-lg'
                    : ''
                }`}
                style={{
                  backgroundColor: activeTab === tab.key ? colors.primary[600] : 'transparent',
                  color: activeTab === tab.key ? 'white' : colors.text.secondary
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== tab.key) {
                    e.currentTarget.style.backgroundColor = colors.neutral[50];
                    e.currentTarget.style.color = colors.primary[600];
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== tab.key) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = colors.text.secondary;
                  }
                }}
              >
                <tab.icon className="h-4 w-4 mr-2 flex-shrink-0" />
                <span className="truncate">{tab.label}</span>
                {tab.key !== 'all' && stats[tab.key] > 0 && (
                  <span className={`ml-2 px-1.5 py-0.5 rounded-full text-xs ${
                    activeTab === tab.key 
                      ? 'bg-white/20 text-white' 
                      : 'bg-theme-neutral-25 text-theme-secondary'
                  }`}>
                    {stats[tab.key]}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Agreements Grid */}
      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-sm text-gray-500">Loading agreements...</p>
        </div>
      ) : !filteredAgreements || filteredAgreements.length === 0 ? (
        <div className="text-center py-12">
          <Package className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {searchTerm ? 'No agreements match your search' : 'No agreements found'}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm
              ? 'Try adjusting your search terms'
              : activeTab === 'all'
                ? 'You don\'t have any agreements yet'
                : `No ${activeTab.replace('_', ' ')} agreements`}
          </p>
          {searchTerm && (
            <div className="mt-4">
              <button
                onClick={() => setSearchTerm('')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <X className="h-4 w-4 mr-2" />
                Clear Search
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredAgreements.map((agreement) => (
            <AgreementCard
              key={agreement.id}
              agreement={agreement}
              onConfirmPickup={handleConfirmPickup}
              onMarkDelivered={handleMarkDelivered}
              onConfirmDelivery={handleConfirmDelivery}
              onCancel={handleCancelAgreement}
              onCreateDispute={() => {
                setSelectedAgreement(agreement);
                setShowDisputeModal(true);
              }}
              onDownloadReceipt={downloadReceiptMutation.mutate}
              isProcessing={confirmPickupMutation.isLoading || markDeliveredMutation.isLoading || confirmDeliveryMutation.isLoading}
              isDownloading={isDownloading}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {(filteredAgreements.length > 0 || true) && ( // Temporarily always show for testing
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-sm" style={{ color: colors.text.secondary }}>
            <span>Showing {startItem}-{endItem} of {filteredAgreements.length} agreements</span>
            <select
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
              className="px-2 py-1 rounded border text-sm"
              style={{
                borderColor: colors.border,
                backgroundColor: colors.neutral[50],
                color: colors.text.primary
              }}
            >
              <option value={12}>12 per page</option>
              <option value={24}>24 per page</option>
              <option value={48}>48 per page</option>
            </select>
          </div>

          {/* Page navigation - only show if multiple pages */}
          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  color: currentPage === 1 ? colors.text.tertiary : colors.text.secondary,
                  backgroundColor: 'transparent'
                }}
                onMouseEnter={(e) => {
                  if (currentPage !== 1) {
                    e.currentTarget.style.backgroundColor = colors.neutral[100];
                    e.currentTarget.style.color = colors.primary[600];
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = currentPage === 1 ? colors.text.tertiary : colors.text.secondary;
                }}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>

              {/* Page numbers */}
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                        currentPage === pageNum
                          ? 'text-white'
                          : 'text-secondary hover:text-primary'
                      }`}
                      style={{
                        backgroundColor: currentPage === pageNum ? colors.primary[600] : 'transparent',
                        color: currentPage === pageNum ? 'white' : colors.text.secondary
                      }}
                      onMouseEnter={(e) => {
                        if (currentPage !== pageNum) {
                          e.currentTarget.style.backgroundColor = colors.neutral[100];
                          e.currentTarget.style.color = colors.primary[600];
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = currentPage === pageNum ? colors.primary[600] : 'transparent';
                        e.currentTarget.style.color = currentPage === pageNum ? 'white' : colors.text.secondary;
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  color: currentPage === totalPages ? colors.text.tertiary : colors.text.secondary,
                  backgroundColor: 'transparent'
                }}
                onMouseEnter={(e) => {
                  if (currentPage !== totalPages) {
                    e.currentTarget.style.backgroundColor = colors.neutral[100];
                    e.currentTarget.style.color = colors.primary[600];
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = currentPage === totalPages ? colors.text.tertiary : colors.text.secondary;
                }}
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Dispute Modal */}
      {showDisputeModal && selectedAgreement && (
        <DisputeModal
          agreement={selectedAgreement}
          onClose={() => {
            setShowDisputeModal(false);
            setSelectedAgreement(null);
            setEvidenceFiles([]);
          }}
          onSubmit={handleCreateDispute}
          evidenceFiles={evidenceFiles}
          setEvidenceFiles={setEvidenceFiles}
          isLoading={createDisputeMutation.isLoading}
        />
      )}

      {/* Confirmation Modal */}
      {showConfirmationModal && (
        <ConfirmationModal
          isOpen={showConfirmationModal}
          onClose={() => setShowConfirmationModal(false)}
          onConfirm={handleConfirmationSubmit}
          title={confirmationModalConfig.title}
          message={confirmationModalConfig.message}
          confirmText={confirmationModalConfig.confirmText}
          cancelText="Cancel"
          type={confirmationModalConfig.type}
          showImageUpload={confirmationModalConfig.showImageUpload}
          showInput={confirmationModalConfig.showInput}
          inputPlaceholder={confirmationModalConfig.inputPlaceholder}
          inputValue={confirmationDescription}
          onInputChange={setConfirmationDescription}
          imageValue={confirmationImages}
          onImageChange={setConfirmationImages}
          maxImages={3}
          isLoading={confirmPickupMutation.isLoading || markDeliveredMutation.isLoading || confirmDeliveryMutation.isLoading || cancelAgreementMutation.isLoading}
          additionalInfo={confirmationModalConfig.additionalInfo}
        />
      )}
    </div>
  );
};

// Agreement Card Component
const AgreementCard = ({
  agreement,
  onConfirmPickup,
  onMarkDelivered,
  onConfirmDelivery,
  onCancel,
  onCreateDispute,
  isProcessing,
  onDownloadReceipt,
  isDownloading
}) => {
  const colors = useThemeColors();

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-theme-primary-50 text-theme-primary-700 border-theme-primary-200';
      case 'in_transit': return 'bg-theme-warning-50 text-theme-warning-700 border-theme-warning-200';
      case 'delivered': return 'bg-theme-info-50 text-theme-info-700 border-theme-info-200';
      case 'completed': return 'bg-theme-success-50 text-theme-success-700 border-theme-success-200';
      case 'cancelled': return 'bg-theme-error-50 text-theme-error-700 border-theme-error-200';
      case 'disputed': return 'bg-orange-50 text-orange-700 border-orange-200';
      default: return 'bg-theme-neutral-50 text-theme-neutral-700 border-theme-neutral-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <Clock className="h-4 w-4" />;
      case 'in_transit': return <Truck className="h-4 w-4" />;
      case 'delivered': return <Package className="h-4 w-4" />;
      case 'completed': return <CheckCircle className="h-4 w-4" />;
      case 'cancelled': return <X className="h-4 w-4" />;
      case 'disputed': return <AlertCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  const canConfirmPickup = agreement.status === 'active' && agreement.is_traveller;
  const canMarkDelivered = agreement.status === 'in_transit' && agreement.is_traveller;
  const canConfirmDelivery = agreement.status === 'delivered' && agreement.is_receiver;
  const canCancel = agreement.status === 'active' && (agreement.is_traveller || agreement.is_sender);
  const canDispute = ['active', 'in_transit', 'delivered'].includes(agreement.status) && (agreement.is_sender || agreement.is_receiver || agreement.is_traveller);

  const timelineEvents = [
    { key: 'created_at', label: 'Created', icon: FileText, color: 'text-theme-success' },
    { key: 'pickup_confirmed_at', label: 'Picked up', icon: CheckCircle, color: 'text-theme-primary' },
    { key: 'delivered_at', label: 'Delivered', icon: Truck, color: 'text-theme-warning' },
    { key: 'receiver_confirmed_at', label: 'Confirmed', icon: CheckCircle, color: 'text-theme-info' },
    { key: 'completed_at', label: 'Completed', icon: CheckCircle, color: 'text-theme-success' }
  ];

  return (
    <div className="bg-white border border-gray-200 rounded-2xl hover:border-blue-300 hover:shadow-xl transition-all duration-300 overflow-hidden group">
      {/* Top accent bar with status color */}
      <div className={`h-1 bg-gradient-to-r ${
        agreement.status === 'active' ? 'from-blue-500 via-blue-400 to-blue-300' :
        agreement.status === 'in_transit' ? 'from-yellow-500 via-yellow-400 to-yellow-300' :
        agreement.status === 'delivered' ? 'from-purple-500 via-purple-400 to-purple-300' :
        agreement.status === 'completed' ? 'from-green-500 via-green-400 to-green-300' :
        agreement.status === 'cancelled' ? 'from-red-500 via-red-400 to-red-300' :
        'from-gray-500 via-gray-400 to-gray-300'
      }`} />

      {/* Content */}
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-50 to-blue-25 rounded-xl flex items-center justify-center border border-blue-200">
              <Package className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                Agreement #{agreement.id}
              </h3>
              <div className="flex items-center space-x-2 mt-1">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(agreement.status)}`}>
                  {getStatusIcon(agreement.status)}
                  <span className="ml-1 capitalize">{agreement.status.replace('_', ' ')}</span>
                </span>
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                  {[
                    agreement.is_sender && 'Sender',
                    agreement.is_receiver && 'Receiver', 
                    agreement.is_traveller && 'Traveller'
                  ].filter(Boolean).join(' & ')}
                </span>
              </div>
            </div>
          </div>

          <div className="text-right">
            <div className="text-xs text-gray-500">
              Created {new Date(agreement.created_at).toLocaleDateString()}
            </div>
            {agreement.deposit_amount > 0 && (
              <div className="text-sm font-medium text-green-600 mt-1">
                Deposit: {agreement.deposit_amount} ETB
              </div>
            )}
          </div>
        </div>

        {/* Route Information */}
        <div className="bg-gray-50 rounded-xl p-4 mb-4 border border-gray-100">
          <div className="flex items-center text-sm text-gray-600 mb-3">
            <MapPin className="h-4 w-4 mr-2 text-green-500" />
            <span className="font-medium text-gray-900">
              {agreement.parcel?.pickup_city || agreement.trip?.origin_city || 'Origin'} → {agreement.parcel?.dropoff_city || agreement.trip?.destination_city || 'Destination'}
            </span>
          </div>
          {/* <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center text-gray-600">
              <Package className="h-4 w-4 mr-2 text-blue-500" />
              <span>{agreement.parcel?.weight_kg || 0} kg • {agreement.parcel?.category || 'General'}</span>
            </div>
            <div className="flex items-center text-gray-600">
              <DollarSign className="h-4 w-4 mr-2 text-green-500" />
              <span>Value: {agreement.parcel?.declared_value || 0} ETB</span>
            </div>
          </div> */}
        </div>

        {/* Participants */}
        <div className="grid grid-cols-3 gap-4 mb-4 text-sm">
          <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-100">
            <p className="font-medium text-blue-700 mb-1">Sender</p>
            <p className="text-blue-600 truncate">{agreement.sender?.first_name} {agreement.sender?.last_name}</p>
            <p className="text-xs text-blue-500 truncate">{agreement.sender?.email}</p>
          </div>
          <div className="text-center p-3 bg-purple-50 rounded-lg border border-purple-100">
            <p className="font-medium text-purple-700 mb-1">Traveller</p>
            <p className="text-purple-600 truncate">{agreement.traveller?.first_name} {agreement.traveller?.last_name}</p>
            <p className="text-xs text-purple-500 truncate">{agreement.traveller?.email}</p>
          </div>
          <div className="text-center p-3 bg-green-50 rounded-lg border border-green-100">
            <p className="font-medium text-green-700 mb-1">Receiver</p>
            <p className="text-green-600 truncate">{agreement.receiver?.first_name || 'Same as sender'}</p>
            <p className="text-xs text-green-500 truncate">{agreement.receiver?.email || agreement.sender?.email}</p>
          </div>
        </div>

        {/* Creative Timeline */}
        <div className="mb-6">
          <h4 className="font-medium text-gray-900 mb-4 flex items-center">
            <Clock className="h-4 w-4 mr-2 text-gray-500" />
            Delivery Progress
          </h4>

          {/* Mobile: Vertical Timeline, Desktop: Horizontal Timeline */}
          <div className="block lg:hidden">
            {/* Mobile/Tablet Vertical Timeline */}
            <div className="space-y-4">
              {timelineEvents.map((event, index) => {
                const eventDate = agreement[event.key];
                const isCompleted = eventDate !== null;
                const isCurrent = !isCompleted && timelineEvents.slice(0, index).every(e => agreement[e.key]);

                return (
                  <div key={event.key} className="flex items-start space-x-4">
                    <div className={`flex-shrink-0 w-10 h-10 rounded-full border-3 flex items-center justify-center transition-all duration-300 ${
                      isCompleted
                        ? 'bg-green-500 border-green-500 text-white shadow-lg'
                        : isCurrent
                          ? 'border-blue-500 text-blue-500 bg-blue-50 animate-pulse shadow-lg'
                          : 'border-gray-300 text-gray-300 bg-white'
                    }`}>
                      <event.icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-medium ${
                        isCompleted ? 'text-green-700' :
                        isCurrent ? 'text-blue-700' :
                        'text-gray-500'
                      }`}>
                        {event.label}
                      </div>
                      {eventDate && (
                        <div className="text-xs text-gray-500 mt-1">
                          {new Date(eventDate).toLocaleDateString()}
                        </div>
                      )}
                      {isCurrent && (
                        <div className="text-xs text-blue-600 font-medium mt-1">
                          In Progress
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Desktop Horizontal Timeline */}
          <div className="hidden lg:block">
            <div className="relative mb-6 overflow-hidden">
              {/* Grid Layout for Timeline Steps */}
              <div className="grid grid-cols-5 gap-4 px-4">
                {timelineEvents.map((event, index) => {
                  const eventDate = agreement[event.key];
                  const isCompleted = eventDate !== null;
                  const isCurrent = !isCompleted && timelineEvents.slice(0, index).every(e => agreement[e.key]);
                  const isUpcoming = !isCompleted && !isCurrent;

                  return (
                    <div key={event.key} className="flex flex-col items-center relative group">
                      {/* Step Circle */}
                      <div className={`relative w-14 h-14 rounded-full border-4 flex items-center justify-center transition-all duration-500 group-hover:scale-110 z-10 ${
                        isCompleted
                          ? 'bg-green-500 border-green-500 text-white shadow-lg shadow-green-200'
                          : isCurrent
                            ? 'border-blue-500 text-blue-500 bg-blue-50 animate-pulse shadow-lg shadow-blue-200'
                            : 'border-gray-300 text-gray-300 bg-white'
                      }`}>
                        <event.icon className={`h-6 w-6 transition-transform duration-300 ${
                          isCompleted ? 'scale-100' : 'scale-90'
                        }`} />

                        {/* Completion Animation */}
                        {isCompleted && (
                          <div className="absolute inset-0 rounded-full bg-green-500 animate-ping opacity-20" />
                        )}
                      </div>

                      {/* Step Label */}
                      <div className="mt-3 text-center min-w-0 flex-1">
                        <div className={`text-sm font-medium transition-colors duration-300 leading-tight break-words ${
                          isCompleted ? 'text-green-700' :
                          isCurrent ? 'text-blue-700' :
                          'text-gray-500'
                        }`}>
                          {event.label}
                        </div>
                        {eventDate && (
                          <div className="text-xs text-gray-500 mt-1">
                            {new Date(eventDate).toLocaleDateString()}
                          </div>
                        )}
                        {isCurrent && (
                          <div className="text-xs text-blue-600 font-medium mt-1 animate-pulse">
                            In Progress
                          </div>
                        )}
                      </div>

                      {/* Desktop Hover Tooltip */}
                      <div className="absolute bottom-full mb-3 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-sm rounded-lg px-4 py-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-20 shadow-lg max-w-xs break-words">
                        {event.label}
                        {eventDate && (
                          <div className="mt-1">
                            {new Date(eventDate).toLocaleString()}
                          </div>
                        )}
                        {!eventDate && (
                          <div className="mt-1 text-gray-300">
                            Pending
                          </div>
                        )}
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Connecting Progress Line */}
              <div className="absolute top-7 left-1/2 transform -translate-x-1/2 w-full max-w-4xl h-0.5 bg-gray-200 rounded-full z-0" style={{ width: 'calc(100% - 7rem)' }}>
                {/* Progress Fill */}
                <div
                  className="h-full bg-green-400 rounded-full transition-all duration-1000 ease-out"
                  style={{
                    width: `${(timelineEvents.filter(event => agreement[event.key]).length / timelineEvents.length) * 100}%`
                  }}
                />
              </div>
            </div>
          </div>

        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-200">
          {canConfirmPickup && (
            <button
              onClick={() => onConfirmPickup(agreement.id)}
              disabled={isProcessing}
              className="flex-1 min-w-[120px] px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center justify-center font-medium"
            >
              {isProcessing ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              Confirm Pickup
            </button>
          )}

          {canMarkDelivered && (
            <button
              onClick={() => onMarkDelivered(agreement.id)}
              disabled={isProcessing}
              className="flex-1 min-w-[120px] px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 transition-colors flex items-center justify-center font-medium"
            >
              {isProcessing ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
              ) : (
                <Truck className="h-4 w-4 mr-2" />
              )}
              Mark Delivered
            </button>
          )}

          {canConfirmDelivery && (
            <button
              onClick={() => onConfirmDelivery(agreement.id)}
              disabled={isProcessing}
              className="flex-1 min-w-[120px] px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center justify-center font-medium"
            >
              {isProcessing ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              Confirm Delivery
            </button>
          )}

          {agreement.status === 'completed' && (
            <button
              onClick={() => onDownloadReceipt(agreement.id)}
              disabled={isDownloading}
              className="flex-1 min-w-[120px] px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors flex items-center justify-center font-medium"
            >
              {isDownloading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
              ) : (
                <FileText className="h-4 w-4 mr-2" />
              )}
              {isDownloading ? 'Downloading...' : 'Download Receipt'}
            </button>
          )}

          {canDispute && (
            <button
              onClick={onCreateDispute}
              className="flex-1 min-w-[120px] px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center justify-center font-medium"
            >
              <AlertCircle className="h-4 w-4 mr-2" />
              Dispute
            </button>
          )}

          {canCancel && (
            <button
              onClick={() => onCancel(agreement.id)}
              className="flex-1 min-w-[120px] px-4 py-2 border border-red-500 text-red-600 rounded-lg hover:bg-red-50 transition-colors flex items-center justify-center font-medium"
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Agreements;
